import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Divider,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import {
  MailOutline as MailIcon,
  Groups as GroupsIcon,
  ShieldOutlined as ShieldIcon,
  ContentCopyOutlined as CopyIcon,
  ArrowRightAlt as ArrowRightAltIcon,
  Save as SaveIcon,
} from "@mui/icons-material";

const formatFullName = (u) => `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();

const CloneUserDialog = ({
  open,
  onClose,
  onApply,
  selectedUser,
  allUsers = [],
}) => {
  const cloneOptions = useMemo(
    () => allUsers.filter((u) => !!u && u.id !== selectedUser?.id),
    [allUsers, selectedUser]
  );

  const [clonePermissions, setClonePermissions] = useState(false);
  const [targets, setTargets] = useState([]);
  const customersCount =
    selectedUser?.customerCount ?? selectedUser?.customers?.length ?? null;

  useEffect(() => {
    if (!open) return;
    setClonePermissions(false);
    setTargets([]);
  }, [open, selectedUser?.id]);

  const handleApply = () => {
    const payload = {
      sourceUserId: selectedUser?.id,
      sourceUserEmail: selectedUser?.email,
      clonePermissions,
      targetUserIds: targets.map((t) => t.id),
    };
    if (onApply) onApply(payload);
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "#2f3b4a",
              boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CopyIcon sx={{ fontSize: 22, color: "#fff" }} />
          </Box>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, fontSize: 20 }}>
          Clone User Profile
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: "text.secondary", mt: 0.5, fontSize: "1.3rem" }}
        >
          Clone this user's settings and permissions to other users
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background:
            "linear-gradient(180deg, #ffffff 0%, #fafafa 60%, #ffffff 100%)",
        }}
      >
        {/* Identity Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2.5,
            borderRadius: 2,
            border: "1px solid #eaeaea",
            boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}
          >
            {formatFullName(selectedUser || {})}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "text.secondary",
            }}
          >
            <MailIcon sx={{ fontSize: 18 }} />

            <Typography variant="body1" sx={{ fontSize: "1.3rem" }}>
              {selectedUser?.email || ""}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
              <GroupsIcon
                sx={{ fontSize: 20, mt: 0.5, color: "text.secondary" }}
              />

              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, fontSize: "1.3rem" }}
                >
                  {customersCount ?? "0"} Customers
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontSize: "1.1rem" }}
                >
                  Active accounts
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontSize: "1.3rem" }}
              >
                {selectedUser?.secondaryEmail
                  ? selectedUser.secondaryEmail
                  : "No Secondary Email"}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "1.1rem" }}
              >
                {selectedUser?.secondaryEmail
                  ? "Secondary email"
                  : "Not configured"}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Permissions Section */}
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2.5, borderRadius: 2, border: "1px solid #eaeaea" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                bgcolor: "rgba(25,118,210,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShieldIcon sx={{ color: "#1976d2", fontSize: 18 }} />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, fontSize: "1.3rem" }}
              >
                Clone User Permissions
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "1.1rem" }}
              >
                Include all role-based access controls and security settings
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={clonePermissions}
                  onChange={(e) => setClonePermissions(e.target.checked)}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 26 } }}
                />
              }
              label={null}
            />
          </Box>
        </Paper>

        {/* Clone To Users section header */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ArrowRightAltIcon sx={{ fontSize: 26, color: "text.secondary" }} />

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, fontSize: 14 }}
            >
              Clone To Users
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "1.1rem", mt: 0.5 }}
          >
            Select multiple users to clone this profile to
          </Typography>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={cloneOptions}
            value={targets}
            onChange={(e, values) => setTargets(values)}
            getOptionLabel={(option) => option.email || formatFullName(option)}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{ fontSize: "1.3rem", paddingTop: 10, paddingBottom: 10 }}
              >
                {option.email || formatFullName(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select users"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1.3rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "1.3rem",
                  },
                  "& .MuiChip-root": {
                    fontSize: "1.25rem",
                  },
                }}
              />
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          color="inherit"
          size="medium"
          sx={{
            fontSize: "1.3rem",
            width: { xs: "100%", lg: "auto" },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={targets.length === 0}
          size="medium"
          sx={{
            fontSize: "1.3rem",
            width: { xs: "100%", lg: "auto" },
          }}
          startIcon={<SaveIcon />}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneUserDialog;
